import React, { } from "react";
import mixpanel from "mixpanel-browser";

import { Container, Divider, Grid } from "semantic-ui-react";

import PlayerDraftCard from "./playerDraftCard";
import CardTable from "./cards/cardCore";
import StageComponent from "./stages/stage";
import ActionMenu from "./actionMenu/actionMenu";

import GameMenu from "./gameMenu";
import PlayerForm from "./preGame/playerForm";

import {
  Action,
  ActionType,
  DefaultDraftOptions,
  DraftData,
  DraftOptions,
  draftToTrackData,
  DraftType,
  Hero,
  Player,
  Team,
} from "./types";
import { AddToHistory, NewRound } from "./utils/roundHandler";
import {
  Cuckio,
  Doppelganger,
  DoppelgangerSwap,
  RandomHero,
  ReRoll,
  Trade,
} from "./utils/cardEvents";

import "./meleeStyle.scss";
import "../meleeStyle/core.scss";
import { useMeleeContext } from "./context/MeleeContextProvider";

import Header from "components/header";
import FFABody from "./ffa/ffaBody";
import { useStopWatch } from "./utils/stopwatch";
import { GetDefaultPlayers } from "./utils/playerUtils";

export default function Melee() {
  const meleeContext = useMeleeContext();
  const round = meleeContext!.gameData;

  let currentTeamsTurn: Team | null = null;
  if (round.CurrentAction) {
    currentTeamsTurn = round.CurrentAction?.Team;
  }

  const { time: blueTime, timerValue: blueTimerValue } = useStopWatch(
    round.Active && currentTeamsTurn === Team.Blue,
    round.BlueTimer ? round.BlueTimer : 0,
  );
  const { time: redTime, timerValue: redTimerValue } = useStopWatch(
    round.Active && currentTeamsTurn === Team.Red,
    round.RedTimer ? round.RedTimer : 0,
  );

  const executeAction = (prevRoundData?: DraftData): void => {
    let temp = prevRoundData ? prevRoundData : Object.assign({}, round);
    temp.CurrentAction = temp.ActionSequence!.pop();

    if (currentTeamsTurn === Team.Blue) {
      temp.BlueTimer = blueTimerValue;
    } else if (currentTeamsTurn === Team.Red) {
      temp.RedTimer = redTimerValue;
    }

    // Dersom det ikke er flere actions, returnerer vi
    // Eller om det ikke er en card effect
    if (
      !temp.CurrentAction ||
      temp.CurrentAction!.Action !== ActionType.CardEffect
    ) {
      meleeContext!.setGameData(temp);
      return;
    }

    switch (temp.CurrentAction!.CardEffect) {
      case "randomHero":
        temp = RandomHero(temp);
        break;
      case "swap":
        swapHeroes(temp.CurrentAction!.Team, temp);
        break;
      case "reroll":
        temp = ReRoll(temp);
        break;
      case "trade":
        temp = Trade(temp);
        break;
      case "doppelganger":
        temp = Doppelganger(temp, temp.CurrentAction!.Team);
        break;
      case "doppelgangerSwap":
        temp = DoppelgangerSwap(temp);
        break;
    }
    temp.SelectedPlayers = [];

    meleeContext!.setGameData(temp);
    executeAction(temp);
  };

  const addPlayerToSelection = (player: Player) => {
    let temp = Object.assign({}, round);
    temp.SelectedPlayers!.push(player);
    meleeContext!.setGameData(temp);
  };

  const addActionToSequence = (action: Action, atStart?: boolean) => {
    let temp = Object.assign({}, round);
    if (atStart && temp.ActionSequence) {
      const firstAction = temp.ActionSequence[0];
      if (firstAction && firstAction.CardEffect === "doppelgangerSwap") {
        //Ensures doppelganger always is last, put incoming action next first
        temp.ActionSequence.splice(1, 0, action);
      } else {
        //Otherwise, put at the end
        temp.ActionSequence = [
          action,
          ...round.ActionSequence!,
        ];
      }
    } else {
      temp.ActionSequence!.push(action);
    }
    meleeContext!.setGameData(temp);
    return temp;
  };

  const setCurrentAction = (
    action: ActionType,
    team: Team,
    player?: number,
  ): void => {
    let temp = Object.assign({}, round);
    temp.CurrentAction = {
      Action: action,
      Team: team,
      PlayerNumber: player,
    };
    meleeContext!.setGameData(temp);
  };

  const applyHeroAction = (
    player: Player,
    hero: Hero,
    action: ActionType,
  ): void => {
    if (!player || !hero) {
      throw new Error("applyHeroPick needs more players and heroes");
    }
    AddToHistory(round);
    let temp = Object.assign({}, round);

    let tempPlayer = temp.Players!.find((p) => p.Number === player!.Number);

    if (action === ActionType.Pick && tempPlayer != null) {
      tempPlayer.PickedHero = hero;
      hero.Picked = true;
    }

    if (action === ActionType.Ban) {
      // Om neste action er cuckio, kjører vi cuckio
      if (
        round.ActionSequence &&
        round.ActionSequence[round.ActionSequence.length - 1].CardEffect ===
        "cuckio"
      ) {
        temp = Cuckio(temp, player, hero);
        meleeContext!.setGameData(temp);
        return;
      }

      if (
        round.DraftType === DraftType.Teams ||
        round.DraftType === DraftType.Duel
      ) {
        let tempHero = tempPlayer!.Draft!.find((h) => h.Name === hero.Name);
        tempHero && tempPlayer!.BannedHeroes!.push(tempHero.Name);
      }
      hero.Banned = true;
    }
    meleeContext!.setGameData(temp);
  };

  const setCardPurchased = (index: number, team: Team) => {
    let temp = Object.assign({}, round);
    const boughtCard = temp.ShopCards!.find((c) => c.Index === index);
    if (!boughtCard) throw new Error("Bought card couldnt be localized");

    boughtCard.Purchased = team;

    meleeContext!.setGameData(temp);
  };

  const swapHeroes = (team: Team, round: DraftData): void => {
    if (!round.Players) throw new Error("Swap failed");
    const players = round.Players.filter((p) => p.Team === team);

    if (players.length === 2) {
      const tempHero = players[0].PickedHero;
      players[0].PickedHero = players[1].PickedHero;
      players[1].PickedHero = tempHero;
    }
    meleeContext!.setGameData(round);
  };

  const updateDraftType = (draftType: DraftType) => {
    let r = Object.assign({}, round);
    r.Players = GetDefaultPlayers();
    r.DraftType = draftType;
    meleeContext?.setGameData(r);
  };

  const startDraft = () => {
    const players = meleeContext!.players;
    if (!players) throw new Error("Could not start draft, players are missing");

    var draftOptions: DraftOptions = meleeContext?.draftOptions
      ? meleeContext.draftOptions
      : DefaultDraftOptions();

    const roundData = NewRound(
      players,
      draftOptions,
      undefined,
      round.DraftType,
    );
    meleeContext!.setGameData(roundData);

    mixpanel.track("Draft Started", { "data": draftToTrackData(roundData) });
  };

  const draftTable = () => {
    return (
      <Grid columns="equal" centered relaxed stackable>
        <Grid.Row>
          <Grid.Column>
            <PlayerDraftCard
              player={round.Players![0]}
              currentAction={round.CurrentAction}
              executeAction={() => executeAction()}
              applyHeroAction={applyHeroAction}
              addPlayerToSelection={addPlayerToSelection}
            />
          </Grid.Column>
          <Grid.Column>
            <PlayerDraftCard
              player={round.Players![2]}
              currentAction={round.CurrentAction}
              executeAction={() => executeAction()}
              applyHeroAction={applyHeroAction}
              addPlayerToSelection={addPlayerToSelection}
            />
          </Grid.Column>
        </Grid.Row>
        <Grid.Row>
          <Grid.Column>
            <PlayerDraftCard
              player={round.Players![1]}
              currentAction={round.CurrentAction}
              executeAction={() => executeAction()}
              applyHeroAction={applyHeroAction}
              addPlayerToSelection={addPlayerToSelection}
            />
          </Grid.Column>
          <Grid.Column>
            <PlayerDraftCard
              player={round.Players![3]}
              currentAction={round.CurrentAction}
              executeAction={() => executeAction()}
              applyHeroAction={applyHeroAction}
              addPlayerToSelection={addPlayerToSelection}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  const duelDraftTable = () => {
    return (
      <Grid columns="equal" centered relaxed stackable>
        <Grid.Row>
          <Grid.Column>
            <PlayerDraftCard
              player={round.Players![0]}
              currentAction={round.CurrentAction}
              executeAction={() => executeAction()}
              applyHeroAction={applyHeroAction}
              addPlayerToSelection={addPlayerToSelection}
            />
          </Grid.Column>
          <Grid.Column>
            <PlayerDraftCard
              player={round.Players![1]}
              currentAction={round.CurrentAction}
              executeAction={() => executeAction()}
              applyHeroAction={applyHeroAction}
              addPlayerToSelection={addPlayerToSelection}
            />
          </Grid.Column>
        </Grid.Row>
      </Grid>
    );
  };

  return (
    <div className="melee-wrapper">
      <Container
        className="regularText"
        style={{ paddingTop: 10, paddingBottom: 10 }}
      >
        <Header
          helmet={`Draft`}
          rightMenu={<GameMenu />}
        />
        {round.Active
          ? (
            <>
              <StageComponent
                stage={round.Stage}
                gameType={round.GameType}
              />
              {round.DraftType === DraftType.FFA
                ? (
                  <FFABody
                    executeAction={() => executeAction()}
                    applyHeroAction={applyHeroAction}
                    addPlayerToSelection={addPlayerToSelection}
                    setCurrentAction={setCurrentAction}
                    setCardPurchased={setCardPurchased}
                    addActionToSequence={addActionToSequence}
                  />
                )
                : (
                  <>
                    <div className="draft-table">
                      {round.DraftType === DraftType.Duel
                        ? <>{duelDraftTable()}</>
                        : <>{draftTable()}</>}
                    </div>

                    <Divider horizontal style={{ color: "white" }}>
                      Shop
                    </Divider>
                    <div className="card-table-container">
                      <CardTable
                        cards={round.ShopCards}
                        setCurrentAction={setCurrentAction}
                        currentAction={round.CurrentAction}
                        setCardPurchased={setCardPurchased}
                        executeAction={executeAction}
                        addActionToSequence={addActionToSequence}
                      />
                    </div>
                  </>
                )}
              <div className="action-menu-padder" />
              <ActionMenu
                CurrentAction={round.CurrentAction}
                ApplyAction={executeAction}
                TournamentGameDetails={meleeContext?.gameData
                  .TournamentGameDetails}
                draftType={round.DraftType}
                TeamBlueTimer={blueTime}
                TeamRedTimer={redTime}
              />
            </>
          )
          : (
            <div>
              <PlayerForm
                draftType={round.DraftType}
                setDraftType={updateDraftType}
              />
              <button onClick={startDraft} className="massive melee-button">
                Draft
              </button>
            </div>
          )}
      </Container>
    </div>
  );
}
